import { gql } from "@apollo/client";
import React, { useCallback } from "react";

import { Txt } from "../../hex-components";
import { useQueryParams } from "../../route/routes";

import { useAuthorizeCliDeviceMutation } from "./CliAuthFlow.generated";
import { CliAuthFlowScreen } from "./CliAuthFlowScreen";
import { CliLogo } from "./CliLogo";

gql`
  mutation AuthorizeCliDevice($userCode: String!, $action: CliDeviceAction!) {
    authorizeCliDevice(userCode: $userCode, action: $action) {
      success
      action
    }
  }
`;

export const CliAuthFlow: React.ComponentType = React.memo(
  function CliAuthFlow() {
    const [authorizeCliDevice, authorizeCliDeviceResult] =
      useAuthorizeCliDeviceMutation();
    const completedAction = authorizeCliDeviceResult.data?.authorizeCliDevice
      .success
      ? authorizeCliDeviceResult.data.authorizeCliDevice.action
      : null;

    const onAuthorize = useCallback(
      async (userCode: string, approve: boolean) => {
        await authorizeCliDevice({
          variables: {
            userCode,
            action: approve ? "APPROVE" : "DENY",
          },
        });
      },
      [authorizeCliDevice],
    );

    const userCodeFromUrl = useQueryParams().get("user_code");
    const errorMessage = authorizeCliDeviceResult.error
      ? parseErrorMessage(authorizeCliDeviceResult.error.message)
      : null;

    return (
      <>
        <CliLogo />

        {!completedAction && (
          <CliAuthFlowScreen.ConfirmUserCode
            userCode={userCodeFromUrl}
            onAuthorize={onAuthorize}
          />
        )}
        {completedAction === "APPROVE" && <CliAuthFlowScreen.Approved />}
        {completedAction === "DENY" && <CliAuthFlowScreen.Denied />}

        {errorMessage && <Txt>{errorMessage}</Txt>}
      </>
    );
  },
);

function parseErrorMessage(errMsg: string): string {
  if (
    errMsg.includes("Invalid or unknown user code") ||
    errMsg.includes("Invalid or expired user code")
  ) {
    return "Invalid code. Please check the code and try again.";
  }
  if (errMsg.includes("expired")) {
    return "This code has expired. Please restart the CLI login process.";
  }
  if (errMsg.includes("already been used")) {
    return "This code has already been used. Please restart the CLI login process.";
  }
  return errMsg;
}
